import { z } from 'zod';
import { castIdSchema } from './cast.js';
import { changesSchema } from './changes.js';
import { SENTENCE_WORDS_MAX, wordsIn } from './coach-voice.js';
import { idSchema, MAX_SOURCE_TITLE, slugSchema } from './common.js';
import { castRoleSchema, contentStatusSchema, difficultySchema, subjectSchema, } from './content.js';
import { problemDetailSchema } from './error.js';
import { RULE_KEY_MAX } from './publish-rules.js';
/**
 * The authoring console's own model — spec 013 §5. Three tables: a job, its rows, and
 * what the reviewer found.
 *
 * Separate from `authoring.ts`, which is *what a generator may write*. These describe the
 * run rather than the curriculum, and nothing here ever reaches a learner.
 *
 * Deliberately not in the package barrel — import `@platform/schema/generation`. Same
 * reason as `content` and `authoring`: the barrel is on the mobile boot path.
 */
/**
 * How many lessons one module may hold — GEN-25.
 *
 * Twenty rather than the twelve a plan used to allow. That bound exists to stop a *model*
 * writing a forty-lesson module; a count a person wrote is a different thing, and the
 * first partner curriculum has two modules of fourteen that it would otherwise refuse.
 * `placeLessonSchema` allows sixty in one collection, so this is the tighter of the two.
 */
export const MAX_LESSONS = 20;
/**
 * How many modules one job takes.
 *
 * Shared with the upload preview so a file that exceeds it is refused at the click rather
 * than by the POST — a lesson-level CSV is many lines per module, and the first partner's
 * 1,463 lines are 253 of these.
 */
export const MAX_ROWS = 50;
/** A curriculum Lesson ID — the plan's own key, and what a built lesson is matched on. */
export const curriculumLessonIdSchema = z.string().min(1).max(64);
/* A module's cast — CAST-06. Here rather than in curriculum.ts so a build row can carry it. */
const castText = z.string().trim().max(500);
export const castAssignmentIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .describe('Chosen by the client and kept for good; lessons name it.');
// Flat rather than a union on `character`: a union body cannot become a DTO. Kept apart
// from its refinement so the planner's proposal can reuse the fields — CURR-44.
const shortFact = z.string().trim().min(1).max(200);
export const castAssignmentFieldsSchema = z.strictObject({
    id: castAssignmentIdSchema,
    character: castIdSchema
        .optional()
        .describe('One of the cast. Omit for a one-off, with `name`.'),
    name: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .optional()
        .describe('A one-off nobody else plays. Omit with `character`.'),
    part: z.enum(['lead', 'supporting']),
    role: castRoleSchema.describe('The role in this module, as a picture names it — Pizzeria owner.'),
    goal: castText.default('').describe('What they want in the scenario.'),
    constraint: castText.default('').describe('What holds them back.'),
    relationship: castText
        .default('')
        .describe('How they stand to the lead. Empty for the lead.'),
    continuity: z
        .array(shortFact)
        .max(10)
        .default([])
        .describe('Short facts every lesson keeps true.'),
});
export const castAssignmentSchema = castAssignmentFieldsSchema.refine((one) => (one.character === undefined) !== (one.name === undefined), 'Give a character or a one-off name, not both.');
export const lessonCastIdsSchema = z
    .array(castAssignmentIdSchema)
    .max(3)
    .refine((ids) => new Set(ids).size === ids.length, 'A lesson names each assignment once.')
    .describe("The module's assignments this lesson uses.");
/**
 * A module's cast as a build captures it — GEN-134. A later edit does not reach the job.
 * The registry is not captured: a planned lesson draws no figure, so it asks no state.
 */
export const buildCastSchema = z.array(castAssignmentSchema).min(1).max(3);
/** The longest writer brief a curriculum lesson may carry into a build — CURR-19. */
export const MAX_WRITER_BRIEF = 4000;
/**
 * One lesson a creator has already decided on — GEN-25.
 *
 * `source` is here rather than only on the row because 37 of the partner's 253 modules
 * cite more than one, and 172 have a lesson with none. A row-level source would throw the
 * difference away, and `content-grounding` is the reason that matters: a CASE scene names
 * where its claim came from, and "somewhere in this module" is not that.
 */
export const lessonBriefSchema = z
    .strictObject({
    title: z.string().min(3).max(120),
    summary: z.string().max(1000).optional(),
    source: z.string().max(500).optional(),
    curriculumLessonId: curriculumLessonIdSchema
        .optional()
        .describe('The curriculum Lesson ID this is built from — CURR-19. Written to the lesson, which is then matched on it rather than on its slug.'),
    brief: z
        .string()
        .max(MAX_WRITER_BRIEF)
        .optional()
        .describe('The writer brief from the curriculum plan.'),
    sources: z
        .array(z.strictObject({
        title: z.string().min(1).max(MAX_SOURCE_TITLE),
        citation: z.string().max(500),
    }))
        .max(5)
        .optional()
        .describe('Every source the plan names for this lesson, with its name. `source` is the one its scenes are grounded in.'),
    storyBeat: z
        .string()
        .max(10_000)
        .optional()
        .describe('Where this lesson sits in its module story, captured when the build was queued — GEN-129.'),
    cast: lessonCastIdsSchema
        .optional()
        .describe("The module's assignments this lesson uses, captured with the cast — GEN-134. Empty uses them all."),
})
    .describe('One lesson, as the creator named it.');
/** The most case facts a lesson build carries from earlier lessons — GEN-150. */
export const MAX_STORY_FACTS = 30;
export const MAX_STORY_FACT = 500;
/**
 * A module's case figures, one per line, saved with its story — GEN-151. Set before a
 * build so lessons written in parallel start from the same price, cost and volume.
 */
export const MAX_STORY_FIGURES = 12;
export const storyFiguresSchema = z
    .array(shortFact)
    .max(MAX_STORY_FIGURES)
    .describe('The case figures every lesson of the module starts from, one per entry — "Menu price: $15 a pizza".');
/** A creator's standing instructions for every lesson of one module — GEN-193. */
export const MAX_WRITER_NOTES = 2000;
export const writerNotesSchema = z
    .string()
    .max(MAX_WRITER_NOTES)
    .describe('Notes for the writer: instructions every lesson built, fixed or reviewed in this module follows — "Every amount is in euros".');
/** A module story as a job captures it — GEN-129. */
export const moduleStorySchema = z.strictObject({
    text: z
        .string()
        .min(1)
        .max(50_000)
        .optional()
        .describe('Absent for a module with writer notes and no story — GEN-193.'),
    revision: z.number().int().min(1),
    notes: writerNotesSchema
        .min(1)
        .optional()
        .describe("The module's writer notes, captured when the job was queued — GEN-193. Absent when none are saved."),
    figures: storyFiguresSchema
        .min(1)
        .optional()
        .describe("The module's saved case figures, captured when the build was queued — GEN-151. Absent when none is saved."),
    facts: z
        .array(z.string().min(1).max(MAX_STORY_FACT))
        .min(1)
        .max(MAX_STORY_FACTS)
        .optional()
        .describe("Figures the module's earlier built lessons state, captured when this lesson's row ran — GEN-150. Absent when none is built, or none states a figure."),
});
/** The story of the module a lesson was built in, and its beat there — GEN-130. */
export const lessonStorySchema = moduleStorySchema.extend({
    beat: z.string().min(1).max(10_000).optional(),
});
/** The most extra story sentences a lesson may spend — `decide-module-story`; their length is `writing-level`'s. */
export const STORY_SENTENCES_MAX = 3;
export const STORY_SENTENCE_WORDS_MAX = SENTENCE_WORDS_MAX;
/**
 * One sentence a lesson adds for its module story beyond the teaching — GEN-131. An
 * author-side record kept on the lesson, never in a field a learner reads.
 */
export const storySentenceSchema = z.strictObject({
    position: z
        .number()
        .int()
        .min(1)
        .describe('The 1-based position of the scene the sentence is in.'),
    field: z
        .string()
        .min(1)
        .max(100)
        .describe('The field holding it, such as "body" or "question.explanation".'),
    sentence: z
        .string()
        .min(1)
        .max(500)
        .describe('The sentence exactly as the field states it.'),
});
/** Room for a reviewer's additions and the sentences a fix cut, which a revert can restore. */
export const STORY_RECORD_MAX = 20;
export const storySentencesSchema = z
    .array(storySentenceSchema)
    .max(STORY_RECORD_MAX);
/** The sentences past the lesson's budget, in lesson order, and every one too long. */
export function storyOverBudget(sentences) {
    const ordered = [...sentences].sort((a, b) => a.position - b.position);
    return {
        extra: ordered.slice(STORY_SENTENCES_MAX),
        long: ordered.filter((one) => wordsIn(one.sentence) > STORY_SENTENCE_WORDS_MAX),
    };
}
/**
 * One row of the ingest CSV — spec 013 §9, and the body the admin posts.
 *
 * It lives here rather than in `packages/generator` because it crosses the wire: the admin
 * parses the CSV in the browser (§11) and posts rows, so the parser and the route have to
 * agree by construction rather than by review.
 *
 * `topic` is optional and the planner supplies it when absent — GEN-22: nobody writing a
 * curriculum thinks in enum values, and the first partner file put the lesson name
 * there, so every row was skipped. `docs/decisions/csv-topic-column.md` for why the column
 * is `topic` while the field it fills is still `subject`.
 */
export const csvRowSchema = z
    .strictObject({
    course: z.string().min(3).max(120).describe('The course title.'),
    module: z.string().min(3).max(120).describe('The module within it.'),
    courseSlug: z
        .string()
        .min(1)
        .max(80)
        .optional()
        .describe('The slug of a course that already exists — CURR-19. A curriculum build names the collection it linked, so a rename never makes a second one.'),
    moduleSlug: z
        .string()
        .min(1)
        .max(80)
        .optional()
        .describe('The same, for the module.'),
    subject: subjectSchema
        .describe('Which topic the course is — the `topic` column. The planner decides when it is absent.')
        .optional(),
    source: z
        .string()
        .max(500)
        .optional()
        .describe('A URL or citation the lessons must be grounded in.'),
    description: z
        .string()
        .max(1000)
        .optional()
        .describe('What this module should cover, in the creator’s words.'),
    difficulty: difficultySchema
        .optional()
        .describe('Overrides the planner, which grades each lesson it writes. Optional because a creator writing forty rows will not grade forty modules — but the one who knows their audience can.'),
    story: moduleStorySchema
        .optional()
        .describe('The module story and its revision, captured when a curriculum build was queued — GEN-129. Authoring context, never learner copy; a later edit does not reach the job.'),
    cast: buildCastSchema
        .optional()
        .describe("The module's cast, captured when a curriculum build was queued — GEN-134. Absent when the module casts nobody, which leaves the planner to cast each lesson."),
    lessons: z
        .array(lessonBriefSchema)
        .min(1)
        .max(MAX_LESSONS)
        .optional()
        .describe('The lessons this module holds, in order, when the creator has already decided them — GEN-25. Absent means the planner decides both the count and the titles.'),
})
    .check((ctx) => {
    // Two lessons of one module whose titles slug the same are one lesson: the writer
    // upserts on slug, so the second would overwrite the first and the run would report
    // both as written. Titles that differ only past the 80-character cap do this — four
    // of the first partner's 1,463 reach it — and nothing downstream could notice.
    // One lesson cannot collide with itself, and on a lesson-level file every line parses
    // as exactly one — this would otherwise slug 1,463 titles looking for nothing.
    if ((ctx.value.lessons?.length ?? 0) < 2)
        return;
    const moduleSlug = moduleSlugOf(ctx.value);
    const seen = new Map();
    for (const [index, lesson] of (ctx.value.lessons ?? []).entries()) {
        const slug = briefSlug(moduleSlug, lesson);
        const first = seen.get(slug);
        if (first === undefined)
            seen.set(slug, lesson.title);
        else
            ctx.issues.push({
                code: 'custom',
                input: ctx.value,
                path: ['lessons', index, 'title'],
                message: `“${lesson.title}” and “${first}” both become ${slug}, so one would overwrite the other.`,
            });
    }
})
    .describe('One row of the ingest CSV.');
/**
 * Which subject a row is about when the row does not say — GEN-22.
 *
 * `ClaudePlanner` decides it properly, having read the brief. This is what the keyless
 * planner uses and what the upload preview shows, so an operator sees the guess before
 * queuing 250 modules rather than after — three callers of one decision, hence one copy.
 *
 * Word matching, because a course title is usually the topic's own name and the cases
 * that are not are a short list rather than a model call. `STRATEGY` is the fallback: the
 * widest of them, and a topic is a colour family, not a claim inside a lesson.
 */
const SUBJECT_CUES = {
    PERSONAL_FINANCE: ['personal finance', 'budget', 'saving', 'retirement'],
    BUSINESS_ANALYTICS: ['analytics', 'metric', 'data analysis', 'dashboard'],
    PRODUCT_MANAGEMENT: ['product management', 'product manager', 'roadmap'],
    PROJECT_MANAGEMENT: ['project management', 'project manager', 'deadline'],
    TEAM_MANAGEMENT: ['team management', 'delegat', 'managing a team'],
    HIRING: ['hiring', 'talent', 'recruit', 'interview'],
    ORGANIZATIONAL_BEHAVIOR: ['organizational', 'organisational', 'culture'],
    CAREER: ['career', 'personal brand', 'resume', 'job search'],
    AI: ['ai\\b', 'genai', 'artificial intelligence', 'machine learning', 'llm'],
    INNOVATION: ['innovation', 'experiment'],
    ECONOMICS: ['economics', 'economy', 'opportunity cost'],
    BUSINESS_FUNDAMENTALS: ['business fundamentals', 'business basics', 'profit'],
    NEGOTIATION: ['negotiat', 'bargain'],
    PUBLIC_SPEAKING: ['public speaking', 'presentation', 'presenting', 'pitch'],
    SALES: ['sales', 'selling', 'pipeline'],
    MARKETING: ['marketing', 'brand', 'positioning', 'advertis'],
    FINANCE: ['finance', 'financial', 'accounting', 'cash', 'valuation'],
    ENTREPRENEURSHIP: ['entrepreneur', 'startup', 'founder', 'venture'],
    LEADERSHIP: [
        'leadership',
        'leading',
        'manager',
        'management',
        'team',
        'coaching',
    ],
    COMMUNICATION: ['communicat', 'writing', 'storytelling', 'feedback'],
    OPERATIONS: [
        'operations',
        'operational',
        'supply chain',
        'process',
        'execution',
    ],
    DECISIONS: ['decision', 'judgement', 'judgment', 'bias'],
    STRATEGY: [
        'strategy',
        'strategic',
        'competitive',
        'business model',
        'governance',
    ],
};
export function subjectFor(row) {
    if (row.subject !== undefined)
        return row.subject;
    // Titles before brief, and `Object.entries` keeps declaration order — both are the
    // ordering. One pass over all three fields read `Strategy` / `Competitive Advantage` as
    // MARKETING, because a sentence about positioning outvoted the word the course is called.
    const match = (text) => Object.entries(SUBJECT_CUES).find(([, cues]) => cues.some((cue) => new RegExp(`\\b${cue}`, 'i').test(text)))?.[0];
    return (match(`${row.course} ${row.module}`) ??
        match(row.description ?? '') ??
        'STRATEGY');
}
/** Fields, honouring quotes. `null` when a quote is left open. */
export function splitLine(line) {
    const fields = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < line.length; i += 1) {
        const char = line.charAt(i);
        if (quoted) {
            if (char !== '"')
                field += char;
            else if (line.charAt(i + 1) === '"') {
                field += '"';
                i += 1;
            }
            else
                quoted = false;
            continue;
        }
        if (char === '"')
            quoted = true;
        else if (char === ',') {
            fields.push(field.trim());
            field = '';
        }
        else
            field += char;
    }
    if (quoted)
        return null;
    fields.push(field.trim());
    return fields;
}
/**
 * The topic a cell means, when it is one or a near miss of one.
 *
 * `FINANCES`, `Finanace` and `marketing` are typos of a topic and are corrected; anything
 * further off is left to the planner, because the column sometimes holds lesson names
 * (GEN-22). A short word is one letter from too many others — IT is not AI — so the
 * slack grows with length, and a tie corrects nothing.
 */
function nearestSubject(given) {
    const cell = given
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '_');
    const exact = subjectSchema.safeParse(cell);
    if (exact.success)
        return exact.data;
    const slack = cell.length <= 3 ? 0 : cell.length < 8 ? 1 : 2;
    const ranked = subjectSchema.options
        .map((subject) => ({ subject, distance: editDistance(cell, subject) }))
        .sort((a, b) => a.distance - b.distance);
    const [best, next] = ranked;
    if (best === undefined || best.distance > slack)
        return undefined;
    return next?.distance === best.distance ? undefined : best.subject;
}
function editDistance(a, b) {
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
        const current = [i];
        for (let j = 1; j <= b.length; j += 1) {
            const swap = a[i - 1] === b[j - 1] ? 0 : 1;
            current[j] = Math.min((previous[j] ?? 0) + 1, (current[j - 1] ?? 0) + 1, (previous[j - 1] ?? 0) + swap);
        }
        previous = current;
    }
    return previous[b.length] ?? 0;
}
const REQUIRED = ['course', 'module'];
// `difficulty` was missing here while `csvRowSchema` accepted it, so a file that used the
// documented column was refused whole: "Header has columns nobody reads: difficulty."
// `topic` was `subject` until GEN-86 — `docs/decisions/csv-topic-column.md`.
const KNOWN = [
    ...REQUIRED,
    'lesson',
    'topic',
    'source',
    'description',
    'difficulty',
];
/**
 * What is wrong with the header, in terms the person who wrote the file can act on.
 *
 * It used to say only which names were absent, which is the least useful half: a
 * curriculum exported with `Course Name` was told `Header is missing: course`, and the
 * first column visibly said Course Name. That reads as a broken tool rather than a
 * rename. So the message names what the file actually has, and what to call it instead.
 */
function headerProblem(raw, columns) {
    const missing = REQUIRED.filter((name) => !columns.includes(name));
    const unknown = columns.filter((name) => !KNOWN.includes(name));
    if (missing.length === 0 && unknown.length === 0)
        return undefined;
    // Over the file's own columns, so the renames come out in the order the reader just
    // saw them listed.
    const renames = raw.flatMap((name) => {
        const want = meant(name);
        return want !== undefined && KNOWN.includes(want) && !columns.includes(want)
            ? [`“${name}” to ${want}`]
            : [];
    });
    return [
        missing.length > 0
            ? `Header is missing: ${missing.join(', ')}.`
            : `Header has columns nobody reads: ${unknown.join(', ')}.`,
        `This file has: ${raw.join(', ')}.`,
        renames.length > 0
            ? `Rename ${renames.join(', ')}.`
            : `The columns it reads are: ${KNOWN.join(', ')}. Only ${REQUIRED.join(' and ')} are required.`,
    ].join(' ');
}
/**
 * The column a header was probably meant to be.
 *
 * The last word, less a trailing `name` — enough for the spellings a spreadsheet
 * produces (`Course Name`, `Lesson Description`) and deliberately no cleverer. It only
 * ever suggests a rename; nothing is accepted on the strength of it.
 */
function meant(header) {
    const words = header
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean);
    const last = words.at(-1);
    return last === 'name' ? words.at(-2) : last;
}
/**
 * The CSV, one result per line — spec 013 §9.
 *
 * Here rather than in `packages/generator` because the admin previews the same file the
 * API will write (§11), and two parsers is not duplication so much as a **divergence**:
 * a naive `split(',')` reads `"Pricing, Strategy"` as two columns where this reads one,
 * so the preview would promise something the write does not do.
 *
 * One bad row never stops the file. A header problem does, because every row is then
 * being read against the wrong columns.
 */
export function readCsv(text) {
    const all = text
        .split(/\r?\n/)
        .map((line, index) => ({ line: index + 1, text: line }))
        .filter((entry) => entry.text.trim().length > 0);
    const [header, ...body] = all;
    if (header === undefined)
        return { lines: [], headerError: 'Empty file.' };
    const raw = splitLine(header.text);
    if (!raw) {
        return { lines: [], headerError: 'Unterminated quote in the header.' };
    }
    const lowered = raw.map((name) => name.toLowerCase());
    const legacy = lowered.includes('subject');
    // Both names fill one field, so whichever won would be a guess and the loser's values
    // would vanish without the person who wrote the file being told.
    if (lowered.includes('topic') && legacy) {
        return {
            lines: [],
            headerError: 'Header has both topic and subject. subject is the old name for topic — keep one.',
        };
    }
    // What the file calls each column, which is what a message about it has to say back.
    // `topic` fills `subject`, renamed per line below.
    const columns = lowered.map((name) => (name === 'subject' ? 'topic' : name));
    const headerError = headerProblem(raw, columns);
    if (headerError !== undefined)
        return { lines: [], headerError };
    // Decided once, from the header. Per cell it is not a decision but a guess: one blank
    // lesson cell in a lesson-level file would read as a module-level row, queue beside the
    // group under the same module slug, and whichever ran second would upsert over the
    // other's lessons.
    const namesLessons = columns.includes('lesson');
    const lines = body.flatMap((entry) => {
        const fields = splitLine(entry.text);
        if (!fields)
            return [{ line: entry.line, error: 'Unterminated quote.' }];
        // `,,,,` is a line a spreadsheet appended, not one anybody wrote.
        if (fields.every((field) => field === ''))
            return [];
        const cells = Object.fromEntries(columns
            .map((name, index) => [name, fields[index] ?? ''])
            .filter(([, value]) => value !== ''));
        const { topic: given, ...record } = cells;
        // A topic that is not a subject, nor a typo of one, is dropped rather than failing
        // the row — GEN-22. The first partner file had the column holding the *lesson* name,
        // and every row was skipped over a value the planner was going to decide anyway.
        const subject = given === undefined ? undefined : nearestSubject(given);
        const ignored = subject === undefined ? given : undefined;
        const corrected = subject !== undefined && subject !== given ? given : undefined;
        if (subject !== undefined)
            record.subject = subject;
        if (namesLessons && record.lesson === undefined) {
            return [{ line: entry.line, error: missing(LABELS.lesson), cells }];
        }
        const parsed = csvRowSchema.safeParse(asRow(record, namesLessons));
        return [
            parsed.success
                ? {
                    line: entry.line,
                    row: parsed.data,
                    ...(ignored === undefined ? {} : { topicIgnored: ignored }),
                    ...(corrected === undefined ? {} : { topicCorrected: corrected }),
                }
                : { line: entry.line, error: describeIssues(parsed.error), cells },
        ];
    });
    return {
        lines: grouped(lines),
        ...(legacy && {
            headerNotice: 'This file calls the topic column “subject”, its old name. It reads the same and nothing is dropped — rename the header to topic the next time you export.',
        }),
    };
}
const LABELS = {
    course: 'course name',
    module: 'module name',
    lesson: 'lesson title',
};
const missing = (label) => `No ${label} — add one or remove the row.`;
/**
 * A skipped line's reason, in words the person who wrote the file can act on. Zod's own
 * messages read `module: Invalid input`, which names neither what is wrong nor the fix.
 */
const describeIssues = (error) => error.issues
    .map((issue) => {
    const field = issue.path.join('.');
    const label = LABELS[field];
    if (label !== undefined) {
        if (issue.code === 'invalid_type')
            return missing(label);
        if (issue.code === 'too_small')
            return `The ${label} is under ${String(issue.minimum)} characters — lengthen it or remove the row.`;
        if (issue.code === 'too_big')
            return `The ${label} is over ${String(issue.maximum)} characters — shorten it.`;
    }
    return `${field}: ${issue.message}.`;
})
    .join(' ');
/**
 * One CSV line as a row — GEN-25.
 *
 * With no `lesson` column this is the record as read. With one, the line describes a
 * *lesson*, so `description` and `source` belong to it rather than to the module: the two
 * files differ in what those columns mean, and reading them the same way is how a lesson
 * brief would be filed as a module summary.
 */
function asRow(record, namesLessons) {
    if (!namesLessons || record.lesson === undefined)
        return record;
    const { lesson, description, source, ...rest } = record;
    return {
        ...rest,
        lessons: [
            {
                title: lesson,
                ...(description === undefined ? {} : { summary: description }),
                ...(source === undefined ? {} : { source }),
            },
        ],
    };
}
/**
 * Lines that name lessons of the same module become one row — GEN-25.
 *
 * Grouped by **slug**, because the slug is what decides whether a write upserts an
 * existing module or creates a second one; two spellings that slug the same are one
 * module, and grouping on the raw title would queue both.
 *
 * Order of first appearance, and lessons in file order. Not restricted to adjacent lines:
 * a file that groups its modules is the normal case, but one that does not is a sort away
 * from being the same curriculum and should not silently become 60 one-lesson modules.
 */
function grouped(lines) {
    const first = new Map();
    const briefs = new Map();
    const out = [];
    for (const line of lines) {
        if (line.row === undefined) {
            out.push(line);
            continue;
        }
        const key = moduleSlugOf(line.row);
        const seen = first.get(key);
        const lessons = line.row.lessons;
        if (seen?.row === undefined) {
            first.set(key, line);
            if (lessons !== undefined)
                briefs.set(key, [...lessons]);
            out.push(line);
            continue;
        }
        // A module-level line has nothing to merge into the first: its description, source
        // and topic would have to beat the other's. So it is skipped, and the file still queues.
        if (lessons === undefined) {
            out.push(skipped(line.line, line.row, sameModule(line.row, seen.line, seen.row)));
            continue;
        }
        // Pushed into one array rather than rebuilt per line: a mis-mapped `module` column
        // collapses a whole file into one group, and copying the accumulated briefs each time
        // does that quadratically before `MAX_LESSONS` ever gets to refuse it.
        briefs.get(key)?.push(...lessons);
        // A module-level field the first line left blank is taken from a later one. Only the
        // lesson columns differ down a module; dropping the rest would lose a `difficulty` a
        // creator set on line four because line one happened to be empty.
        seen.row = { ...line.row, ...seen.row };
    }
    return out.map((line) => {
        if (line.row?.lessons === undefined)
            return line;
        const merged = briefs.get(moduleSlugOf(line.row)) ?? line.row.lessons;
        // Re-parsed only where lines actually merged. The per-line pass already validated a
        // one-lesson row, and `MAX_LESSONS` and the slug-collision check are properties of the
        // group — neither can newly fail on a module that turned out to hold one lesson.
        if (merged.length === line.row.lessons.length)
            return line;
        const parsed = csvRowSchema.safeParse({ ...line.row, lessons: merged });
        return parsed.success
            ? { ...line, row: parsed.data }
            : skipped(line.line, line.row, describeIssues(parsed.error));
    });
}
const skipped = (line, row, error) => ({
    line,
    error,
    cells: { course: row.course, module: row.module },
});
const spelled = (row) => `${row.course} › ${row.module}`;
function sameModule(row, firstLine, first) {
    const same = `Same module as line ${String(firstLine)}`;
    // A different spelling is named, or the operator cannot see why the two collide.
    return spelled(row) === spelled(first)
        ? `${same} — remove this line or merge the two.`
        : `${same}: “${spelled(row)}” and “${spelled(first)}” both become ${moduleSlugOf(row)}. Remove this line or merge the two.`;
}
/**
 * How a row becomes a slug, and therefore what makes a re-run an upsert rather than a
 * duplicate — spec 013 §9.
 *
 * Here rather than in `packages/generator` because three callers have to agree: the admin
 * previewing what a row will do, the API queuing it, and the generator writing it. Two
 * implementations of this is not a style problem, it is a duplicated course.
 */
export function slugify(...parts) {
    return parts
        .join(' ')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/-+$/, '');
}
/**
 * The two slugs a row writes, derived once — GEN-25.
 *
 * Four places composed this by hand: both planners, the upload preview, and the reader's
 * own grouping. Grouping is the one that makes it load-bearing — lines are collapsed by
 * the slug because the slug is what decides whether a write upserts or duplicates, so a
 * fifth spelling would queue two modules the writer then merges.
 */
export const moduleSlugOf = (row) => row.moduleSlug ?? slugify(row.course, row.module);
export const rowSlugs = (row) => ({
    course: row.courseSlug ?? slugify(row.course),
    module: moduleSlugOf(row),
});
/**
 * A lesson's slug, and therefore what a re-run upserts.
 *
 * Named rather than inlined because `csvRowSchema` refuses two lessons that land on the
 * same one, and a check that composed the slug differently from the planners would refuse
 * the wrong rows.
 */
export const lessonSlug = (moduleSlug, title) => slugify(moduleSlug, title);
/**
 * A curriculum lesson's slug leads with its Lesson ID — CURR-19. It is matched on that id,
 * so the slug only has to be unique, and two planned lessons may share a title.
 */
export const briefSlug = (moduleSlug, brief) => brief.curriculumLessonId === undefined
    ? lessonSlug(moduleSlug, brief.title)
    : slugify(brief.curriculumLessonId, brief.title);
export const generationJobIdSchema = idSchema
    .describe('One CSV upload, or one regeneration.')
    .brand();
export const generationRowIdSchema = idSchema
    .describe('One CSV row within a job.')
    .brand();
export const contentReviewIdSchema = idSchema
    .describe('One finding against one piece of content.')
    .brand();
export const jobKindSchema = z
    .enum(['INGEST', 'REGENERATE', 'VERIFY', 'REVIEW'])
    .describe('A whole CSV, one piece of content rewritten, built lessons verified, or one lesson reviewed again.');
export const jobPrioritySchema = z
    .enum(['NORMAL', 'HIGH'])
    .describe('Regeneration is HIGH because somebody is waiting for it; ingest is not.');
export const jobStatusSchema = z
    .enum([
    'QUEUED',
    'RUNNING',
    'REVIEWING',
    'SUCCEEDED',
    'PARTIAL',
    'FAILED',
    'CANCELLED',
])
    .describe('Where a job is. REVIEWING: every other row has settled and only the reviews are left.');
export const rowStatusSchema = z
    .enum(['PENDING', 'RUNNING', 'SUCCEEDED', 'PARTIAL', 'FAILED', 'SKIPPED'])
    .describe('Per row, so a restart resumes at the first that is not SUCCEEDED. PARTIAL means some of the module’s lessons were written and kept — GEN-36.');
/**
 * What one row is a unit of — GEN-92, reasoning in docs/decisions/lesson-sized-jobs.md.
 *
 * `RESEARCH` gathers a module's evidence pack before its outline (spec 018 §4); `PLAN`
 * takes the outline turn and fans out; each `LESSON` is one turn and one lesson;
 * `REVIEW` judges the module once its lessons are written. `MODULE` is the unit this
 * started with — one row that did all of it, which `pnpm ingest` still runs. `SCENE` is a
 * regeneration, which was never module-shaped.
 */
export const rowKindSchema = z
    .enum(['MODULE', 'RESEARCH', 'PLAN', 'LESSON', 'REVIEW', 'SCENE', 'VERIFY'])
    .describe('What one row is a unit of — one lesson, since GEN-92.');
/**
 * Which step of its work a row is in — CURR-27. Stored as the runner reaches each step,
 * because the console derived it from counts that two of the steps never move.
 */
export const rowStageSchema = z
    .enum([
    'READING_PLAN',
    'WRITING_SCENES',
    'ILLUSTRATING',
    'REVIEWING',
    'REVISING',
    'RECHECKING',
    'SAVING',
    'READING_CONTENT',
    'IDENTIFYING_CLAIMS',
    'ACCESSING_SOURCES',
    'COMPARING_EVIDENCE',
    'CHECKING_QUALITY',
    'PREPARING_FINDINGS',
])
    .describe('The step a row is in, or the last one it reached once it has settled.');
export const rowActivitySchema = z
    .enum(['WORKING', 'RETRYING', 'BLOCKED'])
    .describe('What an unsettled row is doing: running, waiting on its next attempt, or held back by something outside it.');
/**
 * Where each row of an import sits in the queue — GEN-92, reasoning in
 * docs/decisions/lesson-sized-jobs.md.
 *
 * The queue claims the lowest-position `PENDING` row, so these functions *are* the order a
 * bulk import runs in: every module's research, then every outline, then each module's
 * lessons, then its review. Ordering only — which module a row belongs to is stored on
 * the row.
 */
export const ROW_STRIDE = MAX_LESSONS + 1;
/** Research first, one row per module, before any outline — GEN-215. */
export const researchPosition = (module) => module;
/** Then the outline rows, one per CSV row, in the order the file listed them. */
export const planPosition = (module) => MAX_ROWS + module;
/**
 * Then each module's lessons, in outline order, after every outline has run. Keyed on the
 * outline row's position, which is what fans them out.
 */
export const lessonPosition = (plan, lesson) => MAX_ROWS + plan * ROW_STRIDE + lesson;
/** Then its review, last in its module's stride, so every lesson of it runs first. */
export const reviewPosition = (plan) => MAX_ROWS + plan * ROW_STRIDE + MAX_LESSONS;
/** A build-and-verify job's verify rows, after every module's lessons and review — CURR-31. */
export const verifyPosition = (lesson) => reviewPosition(planPosition(MAX_ROWS)) + lesson;
/** One fact a module's research found, quoted from its page — GEN-215, spec 018 §4. */
export const evidenceFactSchema = z
    .strictObject({
    claim: z
        .string()
        .trim()
        .min(1)
        .describe('What the fact says, as a lesson could state it.'),
    url: z.url().describe('The page the quote is on.'),
    quote: z
        .string()
        .trim()
        .min(1)
        .describe('The exact words on that page that support the claim.'),
    limits: z
        .string()
        .describe('What the fact does not show: its date, sample or scope.'),
})
    .describe('One quoted fact in a module’s evidence pack.');
export const evidencePackSchema = z
    .array(evidenceFactSchema)
    .describe('What research found for one module. Empty when it found nothing.');
/**
 * Whether a failure means the account is spent — spec 013 §15.
 *
 * Shared because two places act on the same answer: the runner pauses the queue, and the
 * console explains the wait. Two copies of the list is how a console explains a limit the
 * runner never paused on.
 *
 * Prose rather than a code, because the console reads it from a stored row's error text:
 * `claudeAnswers` words a 429, a 402 or a spent credit as "usage limit", and rows from
 * before GEN-127 hold the Agent SDK's spellings.
 */
export function looksRateLimited(message) {
    const text = message.toLowerCase();
    return (text.includes('usage limit') ||
        text.includes('rate limit') ||
        text.includes('quota') ||
        text.includes('429') ||
        text.includes('max_budget'));
}
/** `looksRateLimited` over whatever was thrown. */
export const isRateLimited = (error) => looksRateLimited(error instanceof Error ? error.message : String(error));
export const reviewTargetSchema = z
    .enum(['COLLECTION', 'LESSON', 'SCENE', 'QUESTION'])
    .describe('Which table `targetId` points at.');
export const severitySchema = z
    .enum(['BLOCKER', 'WARN', 'NOTE'])
    .describe('Advice, not a gate (spec 013 §8.3). The publish rules decide.');
/**
 * Which of the six quality questions a finding answers — GEN-89, GEN-93.
 *
 * The categories are the scorecard: `qaScorecard` scores each one from the findings
 * carrying it, so this enum and the weights in `qa-score.ts` are two halves of one rule.
 * A seventh is a migration and a weight, never one without the other. `COMPREHENSION`
 * asks whether a learner can act on the screen, not whether the sentence reads well.
 *
 * Optional wherever a finding appears, because a reviewer that omits it must still be
 * heard: dropping the finding to protect the arithmetic would trade an opinion about the
 * content for a tidier number. `qaScorecard` counts what it cannot place separately.
 */
export const qaCategorySchema = z
    .enum([
    'GROUNDING',
    'COMPREHENSION',
    'INSTRUCTIONAL_DESIGN',
    'MECHANICS',
    'WRITING',
    'RELEVANCE',
])
    .describe('Which of the six quality questions the finding answers.');
/** Why the second model answered nothing — GEN-166. Read from rows written before GEN-147. */
export const secondModelFailureSchema = z
    .enum(['OUT_OF_CREDITS', 'UNREACHABLE', 'TIMED_OUT'])
    .describe('Why the second model gave no answer.');
/**
 * What the human did about it — `PROPOSED` added by GEN-76, and `SUPERSEDED` when a rebuild
 * replaced what it was raised against, which nobody did anything about — CURR-51.
 *
 * An editor rewrite lands as `PROPOSED`, not `ACCEPTED`: the content is live so the
 * preview shows it, but nobody has agreed to it yet. §8.3 keeps the reviewer advisory,
 * and an edit made on its advice cannot resolve a finding the advice could not.
 */
export const findingStatusSchema = z
    .enum(['OPEN', 'PROPOSED', 'ACCEPTED', 'DISMISSED', 'SUPERSEDED'])
    .describe('What the human did about it.');
/**
 * What a build's second review made of a first-review finding, or `NEW` for one only the
 * second review raised — GEN-155. Absent on a finding no automatic repair round judged.
 */
export const findingOutcomeSchema = z
    .enum(['FIXED', 'UNRESOLVED', 'DISPUTED', 'NEW'])
    .describe('What the second review made of the finding after the automatic repair.');
/**
 * Who raised a finding — LOOP-104. Null on the publish gate's own and on older rows. The
 * `JEV_*` values are history: nothing has written one since GEN-147 removed the second model.
 */
export const findingSourceSchema = z
    .enum(['REVIEWER', 'JEV_LABELS', 'JEV_CLAIMS', 'JEV_RUBRIC'])
    .describe('Which check raised the finding.');
/**
 * The statuses a runner may leave behind when it stops.
 *
 * A value rather than a comment, because 013 §5's boot recovery has to answer "is this job
 * finished". Answering it by listing the live ones inverts the moment a status is added.
 */
export const terminalJobStatuses = new Set([
    'SUCCEEDED',
    'PARTIAL',
    'FAILED',
    'CANCELLED',
]);
export const isTerminal = (status) => terminalJobStatuses.has(status);
/**
 * The row statuses a person may ask for again — GEN-92.
 *
 * A value rather than three copies of one `||`, for `terminalJobStatuses`' reason: the
 * server's `where` clause and the console's button have to agree, and a button offered on
 * a row the server refuses is worse than no button.
 *
 * `SKIPPED` is in it because cancelling is not a failure: those lessons never ran.
 */
export const retryableRowStatuses = new Set([
    'FAILED',
    'PARTIAL',
    'SKIPPED',
]);
export const isRetryable = (status) => retryableRowStatuses.has(status);
/** How many of a tally could be asked for again — what decides whether to offer at all. */
export const retryableRows = (tally) => tally.failed + tally.partial + tally.skipped;
/**
 * How many rows stand where — GEN-92, and what the progress screen is made of.
 *
 * Counted rather than stored, because a stored tally is a second copy of the row table
 * that drifts the moment a runner writes a status without also writing here. The same
 * shape is reported twice on a job: over every row, and over the `LESSON` rows alone,
 * which is what "36 of 50 lessons" is asking about.
 */
export const rowTallySchema = z
    .strictObject({
    total: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    succeeded: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
})
    .describe('Rows by status.');
/**
 * The lessons an import actually produced — what the progress bar measures.
 *
 * Deliberately *not* every settled row. A failed lesson and a cancelled one are both
 * finished with, neither exists, and both are in `retryableRowStatuses` — so counting them
 * as progress told an operator who cancelled at 35 of 100 that their import was "100%
 * complete". The bar now stops short by exactly the amount still wanting a decision, and
 * the counts beside it say which kind.
 *
 * `partial` is deliberately in both this and `retryableRowStatuses`: a lesson that wrote
 * some of its module is work you have and work you may want again.
 */
export const producedRows = (tally) => tally.succeeded + tally.partial;
/**
 * The lessons something was spent on, which is what a duration can be averaged over.
 *
 * Not exported: `etaSeconds` below is the only thing that wants this reading, and a third
 * public way to count a tally is a third one a caller has to choose between.
 */
const attemptedRows = (tally) => producedRows(tally) + tally.failed;
/** Nothing to divide by is 0%, not NaN — a queued import has written nothing. */
export const percentDone = (tally) => tally.total === 0 ? 0 : Math.round((producedRows(tally) * 100) / tally.total);
/**
 * Roughly how much longer, in seconds — GEN-92.
 *
 * The mean duration of the rows that have settled, times the ones that have not. It
 * answers the one thing an operator actually asks — minutes or hours — and says nothing at
 * all until one has settled, because an estimate from no samples reads as knowledge.
 *
 * Derived on the screen rather than sent: everything it reads already crosses the wire, so
 * a computed field would be a third answer to a question two others already have, and it
 * would only be as fresh as the last poll.
 */
export function etaSeconds(tally, elapsedMs) {
    // Averaged over the lessons something was spent on, and applied to the ones still in
    // the queue. A cancelled lesson consumed nothing and is waiting for nobody, so it
    // belongs on neither side of the division.
    //
    // Nothing running is a queue that is not moving, and a rate of progress from it is a
    // number the page does not have — GEN-113.
    const done = attemptedRows(tally);
    const left = tally.pending + tally.running;
    if (done === 0 || tally.running === 0 || elapsedMs <= 0)
        return undefined;
    return Math.round((elapsedMs / done) * left * 0.001);
}
/**
 * Whether the lesson in flight has outlived the estimate — GEN-113.
 *
 * `etaSeconds` charges each lesson left one mean duration, so a running lesson older than
 * the mean has already put the job past what it said, and printing it again would be
 * printing a number known to be wrong.
 */
export function pastEstimate(tally, elapsedMs, runningMs) {
    const done = attemptedRows(tally);
    return done > 0 && runningMs > elapsedMs / done;
}
export const generationJobSchema = z
    .strictObject({
    id: generationJobIdSchema,
    kind: jobKindSchema,
    priority: jobPrioritySchema,
    status: jobStatusSchema,
    planner: z
        .string()
        .min(1)
        .describe('Which LessonPlanner ran. Not a model name — OutlinePlanner calls none.'),
    skillSet: z
        .array(z.string().min(1))
        .describe('The resolved skill names, in the order they were loaded.'),
    skillDigest: z
        .string()
        .min(1)
        .describe('Content hash of that set, so a tuned skill is a visible difference between runs.'),
    reviewerSkillSet: z
        .array(z.string().min(1))
        .describe('The reviewer’s skills — CURR-28. Empty on older jobs.'),
    reviewerSkillDigest: z
        .string()
        .min(1)
        .describe('Content hash of the reviewer’s set, or `none`.'),
    editorSkillSet: z
        .array(z.string().min(1))
        .describe('The editor’s skills — CURR-28. Empty on older jobs.'),
    editorSkillDigest: z
        .string()
        .min(1)
        .describe('Content hash of the editor’s set, or `none`.'),
    rowsTotal: z.number().int().min(0),
    rowsSucceeded: z
        .number()
        .int()
        .min(0)
        .describe('So the console can say "3 of 5" rather than only a status.'),
    // What the person who uploaded the file calls it — GEN-92. Import history is a list
    // somebody has to recognise their own upload in, and eight characters of a cuid is
    // not that. Stored rather than derived on read: the courses a job named are in its
    // rows' `input`, and reading fifty JSON blobs to title a list row is not a query.
    label: z
        .string()
        .min(1)
        .describe('What this import is called — its courses, or how many.'),
    modules: z
        .array(z.string().min(1))
        .describe('What a list row is called — GEN-111. The module names in file order, or the lesson a rewrite touches. Empty on jobs queued before it, where `label` is all there is.'),
    fileName: z
        .string()
        .min(1)
        .optional()
        .describe('The uploaded file. Absent for a rewrite and for older jobs.'),
    queuedBy: z
        .string()
        .min(1)
        .optional()
        .describe('The email of the admin who queued it. Absent on older jobs.'),
    research: z
        .boolean()
        .describe('Whether it researched each module first — GEN-218.'),
    // Over the `LESSON` rows alone, which is the unit the acceptance criteria count in:
    // "36 completed, 4 processing, 5 queued". `rowsTotal` counts plan and review rows too,
    // so it answers a different question and both are worth having.
    lessons: rowTallySchema.describe('The lesson rows, by status.'),
    // Optional rather than nullable, and that is a wire decision rather than a style one.
    // `anyOf: [string, null]` is generated as `string | unknown` by the SDK generator, so
    // a nullable field arrives at the client having lost its type entirely. Absent says
    // the same thing and survives the round trip.
    startedAt: z.iso.datetime().optional(),
    finishedAt: z.iso.datetime().optional(),
    error: z.string().optional(),
    createdAt: z.iso.datetime(),
})
    .describe('One run of the generator.');
/**
 * One rule that rejected a planner turn — GEN-88.
 *
 * `problemDetailSchema` plus the group key, because the message alone is not one: it names
 * its observed value, and for the em dash rule that value is the author's prose. Storing
 * `ruleKeyOf`'s answer is what makes the repair cost of a rule a `GROUP BY` rather than a
 * reconstruction. The message is kept whole beside it.
 */
export const planIssueSchema = problemDetailSchema.extend({
    // Bounded because it is a group key, and a key the length of a lesson is not one.
    // `ruleKeyOf` truncates to the same bound, so this is a check that cannot fire on
    // anything it produced rather than one that can cost a row its record.
    rule: z
        .string()
        .max(RULE_KEY_MAX)
        .describe('The rule, as the same sentence every time it fires.'),
});
/**
 * Every planner turn a row had to repair, in the order they were rejected — GEN-88.
 *
 * One inner array per rejected turn, so "which rule cost the most repair *turns*" counts
 * turns rather than issues. A clean row stores `[]`. What the shape leaves out — a lesson
 * — and why the key is derived from prose rather than declared per rule are in
 * `docs/decisions/planner-repair-records.md`.
 */
export const rowRepairsSchema = z.array(z.array(planIssueSchema));
/** One tool call a research row made, as its `PostToolUse` hook saw it — GEN-216. */
export const toolCallSchema = z.strictObject({
    tool: z.string().min(1).describe('The tool, as the agent named it.'),
    input: z
        .string()
        .describe('What it was asked: the query, the address or the claim.'),
    failed: z
        .string()
        .optional()
        .describe('Why the call failed. Absent when it answered.'),
});
export const rowCallsSchema = z.array(toolCallSchema);
export const generationRowSchema = z
    .strictObject({
    id: generationRowIdSchema,
    position: z.number().int().nonnegative(),
    kind: rowKindSchema,
    courseSlug: z.string().min(1),
    moduleSlug: z.string().min(1),
    // Which lesson a `LESSON` row is for, as the outline named it — GEN-92. Absent on the
    // other kinds, which are about the module rather than one lesson in it.
    lessonTitle: z.string().optional(),
    status: rowStatusSchema,
    collectionId: z
        .string()
        .optional()
        .describe('The module this row produced, once it has produced one.'),
    error: z.string().optional(),
    // What a rewrite changed, computed from the copy taken before it — GEN-56. Absent on
    // an INGEST row, which replaced nothing, and on a REGENERATE row that has not run.
    changes: changesSchema.optional(),
    // How far into its module a row has got — GEN-38. `RUNNING` is the same word for
    // eight minutes and for thirty-five, so an operator cannot tell a job that is working
    // from one that is wedged. `planned` is written when the plan comes back and before
    // the first lesson, which is what lets a row that dies at lesson six still say how
    // many it meant to write.
    planned: z
        .number()
        .int()
        .nonnegative()
        .optional()
        .describe('How many lessons the plan committed to; absent until the plan returns.'),
    completed: z
        .number()
        .int()
        .nonnegative()
        .describe('How many of them have been written and attached so far.'),
    writing: z
        .string()
        .optional()
        .describe('The lesson in flight. Absent on a row that is not running.'),
    attempts: z
        .number()
        .int()
        .nonnegative()
        .describe('Pickups, not errors. A redeploy mid-row costs one — 013 §5.'),
    lessonId: curriculumLessonIdSchema
        .optional()
        .describe('The curriculum Lesson ID a build row is for — CURR-27. Absent on an import row.'),
    stage: rowStageSchema.optional(),
    activity: rowActivitySchema.optional(),
    activityReason: z
        .string()
        .optional()
        .describe('Why a row is retrying or blocked. Never model output.'),
    progressAt: z.iso
        .datetime()
        .optional()
        .describe('When the row last changed stage or finished.'),
    // Both, so a row's duration is derivable from the API rather than only from Postgres.
    // They were on the table from the start and simply never mapped out, which is why
    // following the first live run meant diffing `Lesson.createdAt` values by hand.
    startedAt: z.iso.datetime().optional(),
    finishedAt: z.iso.datetime().optional(),
    calls: rowCallsSchema
        .optional()
        .describe('Each search, fetch and save a RESEARCH row made, in order. Absent when it made none.'),
})
    .describe('One CSV row and what became of it.');
/** A lesson as the job detail lists it by outcome — CURR-27. */
export const jobLessonSchema = z.strictObject({
    rowId: generationRowIdSchema,
    lessonId: curriculumLessonIdSchema.optional(),
    name: z.string().min(1),
});
/**
 * What the job is doing right now — CURR-27, from the one row the lane is on, or the row
 * waiting to retry or held back. Absent when nothing in the job is moving.
 */
export const jobCurrentSchema = z
    .strictObject({
    rowId: generationRowIdSchema,
    kind: rowKindSchema,
    stage: rowStageSchema.optional(),
    lessonId: curriculumLessonIdSchema.optional(),
    lessonName: z.string().min(1).optional(),
    activity: rowActivitySchema,
    activityReason: z.string().optional(),
    startedAt: z.iso.datetime().optional(),
    progressAt: z.iso.datetime().optional(),
})
    .describe('The row the job is on, and what it is doing.');
/**
 * A stored finding, and what the editor did about it — SKILL-03.
 *
 * The three trailing fields are a *record*, not an instruction: they are written after an
 * edit has landed, from the copy taken before it. `findingSchema` — what a reviewer
 * returns — is unchanged and still has nowhere to put a rewrite, which is the invariant
 * §8.2 draws. Reasoning in docs/decisions/editor-writes-back.md.
 */
export const contentReviewSchema = z
    .strictObject({
    id: contentReviewIdSchema,
    targetType: reviewTargetSchema,
    targetId: z.string().min(1),
    severity: severitySchema,
    category: qaCategorySchema
        .optional()
        .describe('Which score this finding moves. Absent on a rule’s refusal.'),
    finding: z.string().min(1).describe('What is wrong, in one sentence.'),
    suggestion: z.string().min(1).describe('What to do about it.'),
    status: findingStatusSchema,
    appliedAt: z.iso
        .datetime()
        .optional()
        .describe('When the editor rewrote the target. Absent if it never did.'),
    revertedAt: z.iso
        .datetime()
        .optional()
        .describe('When a human put the previous version back.'),
    // Derived from the snapshot on read, exactly as `generationRowSchema.changes` is: the
    // stored copy holds both sides, and a second stored diff is a copy that can drift.
    changes: changesSchema
        .optional()
        .describe('What the edit changed. Absent until one lands.'),
    editorNote: z
        .string()
        .optional()
        .describe('Why the editor changed nothing, or why the outcome is not FIXED.'),
    outcome: findingOutcomeSchema.optional(),
    lessonVersionId: z
        .string()
        .optional()
        .describe('The saved lesson version the review that raised it read. Absent on older rows.'),
    recheckedVersionId: z
        .string()
        .optional()
        .describe('The saved lesson version the second review read when it gave the outcome.'),
    createdAt: z.iso.datetime(),
})
    .describe('One finding, and the edit it produced if the editor applied it.');
/** One review of a lesson: the saved version it read, and what it found — CURR-54. */
export const lessonReviewSchema = z
    .strictObject({
    versionId: z
        .string()
        .min(1)
        .describe('The saved lesson version this review read.'),
    jobId: generationJobIdSchema.describe('The job that ran it.'),
    reviewedAt: z.iso.datetime(),
    findings: z
        .array(contentReviewSchema)
        .describe('What it raised on this version, and the earlier findings it gave an outcome.'),
})
    .describe('One review of a lesson, linked to the version it read.');
export const lessonReviewsSchema = z
    .strictObject({
    outOfDate: z
        .boolean()
        .describe('The lesson’s saved content has changed since the newest review read it. False when it was never reviewed.'),
    reviewing: generationJobIdSchema
        .optional()
        .describe('A review of this lesson queued or running, if there is one.'),
    reviews: z.array(lessonReviewSchema).describe('Newest first.'),
})
    .describe('Every review of a lesson, and whether the newest is out of date.');
/**
 * What a reviewer returns, before anything is stored — spec 013 §8.2.
 *
 * `targetRef` is a slug rather than an id because the reviewer runs on what was written
 * and slugs are what a plan carries; the caller resolves it when it persists a
 * `ContentReview`. That is also why this is a separate shape from `contentReviewSchema`
 * rather than a `.pick()` of it.
 *
 * There is deliberately no field a writer could read as an edit. The reviewer cannot
 * rewrite content because its output has nowhere to put a rewrite.
 */
export const findingSchema = z
    .strictObject({
    targetType: reviewTargetSchema,
    targetRef: z
        .string()
        .min(1)
        .describe('A slug, or `slug#position` for one scene within a lesson.'),
    severity: severitySchema,
    category: qaCategorySchema
        .optional()
        .describe('Which of the five scores this finding moves.'),
    finding: z
        .string()
        .min(1)
        .max(300)
        .describe('What is wrong, in one sentence.'),
    suggestion: z.string().min(1).max(300).describe('What to do about it.'),
    raisedBy: findingSourceSchema.nullable(),
})
    .describe('One finding. Advice — the publish rules are the gate.');
/**
 * Which findings the editor may be *asked* to answer — SKILL-03, widened by GEN-70.
 *
 * `SCENE` because a scene is what a rewrite writes. "This lesson has four scenes and needs
 * five" cannot be answered by rewriting one of them; that stays open for a person.
 *
 * Severity no longer narrows this half: a `WARN` the reviewer agrees with still has to be
 * answered by hand, one scene at a time, which is the work the editor exists to do.
 * Applying stays advisory — `docs/decisions/editor-writes-back.md`, and its amendment.
 */
export const editable = (finding) => finding.targetType === 'SCENE' &&
    !finding.suggestion.startsWith(AUTHOR_SUPPLIES) &&
    !forDeveloper(finding);
/**
 * How a reviewer says the fix needs facts the lesson does not hold — per-scene QA.
 *
 * The editor is never asked to answer one: it would have to invent the competitor prices
 * or sales figures the finding says are missing, and a confident rewrite built on made-up
 * numbers is worse than the gap. The author supplies them, and a later review writes the text.
 */
export const AUTHOR_SUPPLIES = 'Author to supply:';
/**
 * How a reviewer says the fix is an app change — gestures, layout, safe areas — GEN-155.
 * No rewrite of the words can answer one, so the editor is never asked to.
 */
export const DEVELOPER_FIX = 'Developer:';
/** A finding only an app change can answer — `DEVELOPER_FIX`. */
export const forDeveloper = (finding) => finding.suggestion.startsWith(DEVELOPER_FIX);
/**
 * The statuses that leave a blocker standing — GEN-76.
 *
 * `PROPOSED` counts with `OPEN` rather than with `ACCEPTED`. A rewrite nobody has kept is
 * not a fix, and letting it clear the publish gate would make the accept step decorative.
 */
export const UNRESOLVED = ['OPEN', 'PROPOSED'];
/**
 * Which findings a build's repair answers with nobody asking — GEN-155 widened it to `WARN`.
 *
 * Not `NOTE`, the half of SKILL-03's argument that survives: an unattended pass over every
 * `NOTE` rewrites content that was blocking nothing and warning of nothing, across a whole
 * corpus. docs/decisions/editor-writes-back.md has the rest, and its amendments. A question's
 * finding is rewritten through its scene — GEN-191.
 */
export const editedUnasked = (finding) => onScene(finding) &&
    editable({ ...finding, targetType: 'SCENE' }) &&
    finding.severity !== 'NOTE';
/** A finding stored against a scene: a `QUESTION` one names the scene holding it — GEN-191. */
export const onScene = (finding) => finding.targetType === 'SCENE' || finding.targetType === 'QUESTION';
/** POST /authoring/generations — the admin has already parsed the CSV (§11). */
export const startGenerationSchema = z
    .strictObject({
    rows: z.array(csvRowSchema).min(1).max(MAX_ROWS),
    priority: jobPrioritySchema.optional(),
    fileName: z
        .string()
        .min(1)
        .max(255)
        .optional()
        .describe('The uploaded file, so the job list can name it — GEN-111.'),
})
    .check((ctx) => {
    // Two rows writing one module would each spend a generation and the second would
    // upsert over the first. `readCsv` already skips them; this holds for any caller.
    const seen = new Map();
    for (const [index, row] of ctx.value.rows.entries()) {
        const slug = moduleSlugOf(row);
        const first = seen.get(slug);
        if (first === undefined)
            seen.set(slug, index);
        else
            ctx.issues.push({
                code: 'custom',
                input: ctx.value,
                path: ['rows', index, 'module'],
                message: `Rows ${String(first + 1)} and ${String(index + 1)} both write module ${slug}.`,
            });
    }
})
    .describe('A parsed CSV, submitted as a job.');
/**
 * What to write again, and what to change about it — spec 013 §12.1, widened by GEN-47.
 *
 * One shape per grain rather than one with optional ids, because each carries exactly what
 * its copy-on-write needs. A scene is regenerated *for a lesson* and a question *for a
 * scene*: which placement is being reviewed is what decides whether the write forks or
 * lands in place, so leaving it out would make the fork unresolvable.
 *
 * The grains are not `ReviewTarget`. That enum is what a `ContentReview` finding points
 * with, and an option has no row a finding could be filed against; keeping them apart is
 * what lets this widen without a migration.
 */
const id = z.string().min(1);
const sceneGrain = z.strictObject({
    targetType: z.literal('SCENE'),
    lessonId: id,
    sceneId: id,
});
const questionGrain = z.strictObject({
    targetType: z.literal('QUESTION'),
    sceneId: id,
    questionId: id,
});
const explanationGrain = z.strictObject({
    targetType: z.literal('EXPLANATION'),
    sceneId: id,
    questionId: id,
});
/**
 * `lessonId` is carried although the write is to one item, because a scene placed in
 * several lessons forks rather than changing under the others — the same rule the scene
 * grain obeys, and unresolvable without knowing which placement is being reviewed.
 */
const optionGrain = z.strictObject({
    targetType: z.literal('OPTION'),
    lessonId: id,
    sceneId: id,
    itemId: id,
});
const lessonGrain = z.strictObject({
    targetType: z.literal('LESSON'),
    lessonId: id,
});
/** Every grain a rewrite may name, including the whole lesson. */
export const rewriteTargetSchema = z
    .discriminatedUnion('targetType', [
    sceneGrain,
    questionGrain,
    explanationGrain,
    optionGrain,
    lessonGrain,
])
    .describe('Which part of a lesson to write again.');
/**
 * The grains a queued row may hold — every one but the lesson.
 *
 * A row is always one scene. A lesson is expanded into a row per scene when it is queued,
 * so the queue's timeout, retry and per-row status apply to each model turn rather than
 * once to twelve of them.
 */
export const sceneTargetSchema = z.discriminatedUnion('targetType', [
    sceneGrain,
    questionGrain,
    explanationGrain,
    optionGrain,
]);
const instruction = z
    .string()
    .min(1)
    .max(500)
    .describe('What the reviewer wants different, in their own words.');
/**
 * One route rather than one per grain — the shape `submitAnswer` already uses.
 *
 * That endpoint serves every scene mechanic through one nested discriminated union, and
 * `targetType` names the grain in the body either way. The union is nested rather than the
 * body itself because `createZodDto` cannot extend one: a union has no statically known
 * members, so a top-level union DTO does not compile.
 *
 * `instruction` sits beside the target rather than inside each arm. It is the one thing
 * every grain has in common, and five copies of a bound is five places to change it.
 */
export const startRegenerationSchema = z
    .strictObject({ target: rewriteTargetSchema, instruction })
    .describe('POST /authoring/generations/rewrites');
/**
 * What a queued regeneration row holds. Read back by the runner, never sent as a body.
 *
 * `findingId` is what makes the row the editor's rather than the planner's — SKILL-03. The
 * queue, the in-flight lock, the retry and the per-row timeout are the same problem for
 * either agent, so applying a finding rides the machinery a rewrite already has rather
 * than holding an HTTP request open for a model turn.
 */
export const sceneRequestSchema = z.strictObject({
    target: sceneTargetSchema,
    instruction,
    findingId: z
        .string()
        .min(1)
        .optional()
        .describe('The finding this row answers. Absent on a rewrite a person asked for.'),
    story: lessonStorySchema
        .optional()
        .describe('The story of the module the lesson was built in, captured when the row was queued — GEN-130. Absent when that module has none.'),
});
export const discardedSchema = z
    .strictObject({ restored: z.boolean() })
    .describe('Whether the row had a snapshot to put back — GEN-56.');
export const keptSchema = z
    .strictObject({ status: findingStatusSchema })
    .describe('Where the finding stands once the edit was kept — GEN-76.');
export const retriedSchema = z
    .strictObject({ retried: z.number().int().nonnegative() })
    .describe('How many rows were put back on the queue — GEN-92.');
/**
 * POST /authoring/generations/existing — what an upload would write over.
 *
 * A row upserts by slug and leaves anything a human published as it is, so a slug that
 * already exists changes what queuing does. The preview asks before the click.
 */
export const existingSlugsSchema = z
    .strictObject({
    slugs: z
        .array(slugSchema)
        .min(1)
        .max(2 * MAX_ROWS),
})
    .describe('The course and module slugs a CSV would write.');
export const existingCollectionsSchema = z
    .strictObject({
    collections: z.array(z.strictObject({
        slug: slugSchema,
        title: z.string(),
        status: contentStatusSchema,
    })),
})
    .describe('The slugs that already exist. A slug not listed is new.');
export const generationDetailSchema = generationJobSchema
    .extend({
    rows: z.array(generationRowSchema),
    units: rowTallySchema.describe('Every row but the reviews, by the status it is shown with — GEN-113. With `reviews`, each row on the page is counted once.'),
    reviews: rowTallySchema.describe('The review rows, counted apart: a job reads done once its lessons are written, while its review may still be running.'),
    waitingOn: generationJobSchema
        .pick({ id: true, label: true })
        .optional()
        .describe('The job the one lane is working on while this one waits — spec 013 §4.1. A job queued behind another and a stuck one look identical without it.'),
    pausedUntil: z.iso
        .datetime()
        .optional()
        .describe('Set while the queue is waiting on a subscription limit — §15.'),
    current: jobCurrentSchema.optional(),
    progressAt: z.iso
        .datetime()
        .optional()
        .describe('When any row last changed stage or finished. With `startedAt`, how long the job has run and how long since it moved.'),
    completedLessons: z.array(jobLessonSchema),
    failedLessons: z
        .array(jobLessonSchema)
        .describe('Failed, or stopped with only part of their work kept.'),
    remainingLessons: z
        .array(jobLessonSchema)
        .describe('Queued, running, or cancelled — what a retry would build.'),
})
    .describe('A job and every row in it.');
export const generationListSchema = z
    .strictObject({
    jobs: z.array(generationJobSchema.extend({
        mine: z
            .boolean()
            .describe('Whether the caller queued it — GEN-111. Finding your own upload is what the list is for, and newest-first points at somebody else’s once a second admin uploads.'),
    })),
    pausedUntil: z.iso
        .datetime()
        .optional()
        .describe('Set while the queue is waiting on a subscription limit — §15. A paused queue and a stuck one look identical without it.'),
    plannerNext: z
        .string()
        .min(1)
        .describe('Which planner the next job would use. `outline` means a run will write shaped placeholders rather than lessons — worth saying before the click, not only on the job afterwards.'),
    plannerDeclined: z
        .string()
        .min(1)
        .optional()
        .describe('Why the planner was declined although a credential is configured — a subscription token in place of an API key, or a key outside production with no `PLATFORM_SPEND` budget. Absent when there is no credential at all, which is the other way `plannerNext` becomes `outline`.'),
})
    .describe('Jobs, newest first, and what the next one would run with.');
export const generationPromptSchema = z
    .strictObject({
    digest: z
        .string()
        .describe('A job’s `skillDigest`, `reviewerSkillDigest` or `editorSkillDigest`.'),
    text: z
        .string()
        .describe('The whole system text the agent ran with. It hashes to `digest`.'),
})
    .describe('The system prompt behind a skill digest — GEN-192.');
export const systemPromptGroupSchema = z
    .enum(['lessons', 'modules', 'collections', 'images', 'learner'])
    .describe('Where an agent is used: `collections` is courses and paths, `learner` the app.');
export const agentNameSchema = z
    .enum([
    'plan',
    'outline',
    'review',
    'edit',
    'fix',
    'fix-visual',
    'recast',
    'draft-story',
    'course-write',
    'course-review',
    'chat',
    'illustrate',
    'tutor',
    'tutor-check',
    'consultant',
    'train-brief',
    'train-audit',
    'train-suggest',
    'train-twist',
    'counterpart',
    'debrief',
    'train-help',
])
    .describe('Every agent that calls a model.');
export const systemPromptAgentSchema = z
    .strictObject({
    agent: agentNameSchema,
    label: z.string(),
    group: systemPromptGroupSchema,
    usedIn: z
        .string()
        .describe('The features that call it, such as "Lesson build".'),
    skills: z
        .array(z.string())
        .describe('The skill files inlined in its system text. Empty for a role.'),
    digest: z
        .string()
        .describe('The hash of its system text, the one in `texts`. For the planner, reviewer and editor it is the digest a job records.'),
    taskRules: z
        .array(z.string())
        .optional()
        .describe('Fixed rules sent beside the per-lesson parts, which are not shown. `perRequest` says which requests carry them.'),
    perRequest: z
        .string()
        .optional()
        .describe('Where the per-request parts that are not shown come from.'),
    preset: z
        .strictObject({
        characters: z.int().positive(),
        claudeCode: z.string().describe('The Claude Code version measured.'),
    })
        .optional()
        .describe('The Claude Code system prompt sent before its own text. Admin agents only — GEN-214.'),
})
    .describe('One agent that calls a model — ADMIN-26.');
export const systemPromptsSchema = z
    .strictObject({
    agents: z.array(systemPromptAgentSchema),
    answerRule: z
        .string()
        .describe('The line the Messages API adds after a learner agent’s system text when it asks for a structured answer.'),
    texts: z
        .array(generationPromptSchema)
        .describe('Each distinct system text once, keyed by digest.'),
})
    .describe('Every agent’s current system prompt — ADMIN-26.');
//# sourceMappingURL=generation.js.map